import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  budgets as budgetsApi,
  budgetTransactions as transactionsApi,
  expenses as expensesApi,
  donations as donationsApi,
  collections as collectionsApi
} from '../../api/endpoints';
import api, { getList } from '../../api/client';
import {
  useFetchList, useSearch, usePagination, useNotification, useOptionList,
  useOpenRecordFromLink, RECORD_GONE
} from '../../hooks';
import { formatDate, formatCurrency, formatCurrencyPrecise, todayISO, currencySymbol } from '../../utils/format';
import { compactAxisTick } from '../financial/analytics';
import { getStoredUser, isGlobalReadOnly } from '../../utils/auth';
import { hexToRgba } from '../../theme/tokens';
import { useReadOnly } from '../../utils/readOnly';
import useStyles from '../../theme/useStyles';
import { currentFinancialYear, nextFinancialYear, annualOf } from './variance';
import {
  buildDocumentVariance, varianceTotals, monthlyBurn, headNamesIn, headsIn, itemNamesUnder,
  adjustmentsByHead, FY_MONTHS, UNBUDGETED_HEAD
} from '../expenses/budgetLines';
import type { BudgetLineVariance, VarianceRowKind } from '../expenses/budgetLines';
import { budgetHeads } from '../../api/endpoints';
import type { BudgetDocument as BudgetTree } from '../../types';
import {
  liveByStatus, voidedByStatus, deleteRequestedByStatus, isDeleteRequested, isVoidStatus, requestDeletion, approveDeletion, keepRecord
} from '../../utils/records';
import BudgetDocument from './BudgetDocument';
import {
  PageHeader, SubTabs, SearchBar, DataTable, Pagination, Notification,
  PromptDialog, ConfirmDialog, FormLayout, Input, Select, SearchableSelect, Badge, Button, Icon, PrintButton
} from '../../components/ui';
import type { IconName } from '../../components/ui';
import type {
  Budget, BudgetTransaction, Expense, Donation, Collection,
  VarianceRow, WorkflowStatus, HeadType
} from '../../types';
import type { Column } from '../../components/ui';

// The stored values stay Income/Expense (the report keys on them); the words
// people see say what they do to the head's budget.
const TXN_TYPES = [
  { value: 'Income', label: 'Supplementary — adds to the head\'s budget' },
  { value: 'Expense', label: 'Charge — counts as spent against the head' }
];
const TXN_TYPE_LABEL: Record<string, string> = { Income: 'Supplementary', Expense: 'Charge' };
const AMOUNT_BASIS = [
  { value: 'monthly', label: 'Per month' },
  { value: 'onetime', label: 'One-time (annual)' }
];

const EMPTY_BUDGET: BudgetForm = { fy: '', department: '', head: '', amount: '', isOneTime: false, notes: '' };
const EMPTY_TXN: TxnForm = { fy: '', head: '', date: todayISO(), amount: '', type: 'Income', voucherNo: '', notes: '' };

/** A proposal's annual figure: per month unless one-time. Rows from before the flag existed are annual. */
const annualOfProposal = (b: Pick<Budget, 'amount' | 'isOneTime'>): number => {
  const n = parseFloat(String(b.amount ?? 0)) || 0;
  return b.isOneTime === false ? n * 12 : n;
};

/** Adjustments saved before approval existed carry no status and count as approved. */
const approvalOf = (t: Pick<BudgetTransaction, 'approvalStatus'>): WorkflowStatus =>
  t.approvalStatus == null ? 'Approved' : t.approvalStatus;

/**
 * Plainer wording on screen only. The stored values stay 'Pending' /
 * 'Approved' / 'Rejected' — the approval logic and existing rows compare
 * against those strings — but "Pending" alone did not say what was being
 * waited on (CHM-166).
 */
const STATUS_LABELS: Record<string, string> = {
  Pending: 'Awaiting approval',
  Approved: 'Approved',
  Rejected: 'Rejected'
};
const statusLabel = (status?: string) =>
  STATUS_LABELS[String(status || 'Pending')] || String(status || 'Pending');

const toneOf = (status?: string): 'success' | 'error' | 'warning' =>
  (status === 'Approved' ? 'success' : status === 'Rejected' ? 'error' : 'warning');

/** Whatever the list's selected row was, so an edit re-sends the fields the form does not show. */
type Approvable = { kind: 'budget'; row: Budget } | { kind: 'txn'; row: BudgetTransaction };

/** Budget-vs-actual needs both budget resources plus every finance record. */
type FinanceRow = (Budget | BudgetTransaction | Expense | Donation | Collection) & {
  __key: 'budgets' | 'transactions' | 'expenses' | 'incomes';
};

/** Editable fields; the human ID appears only when editing. */
type BudgetForm = Partial<Budget> & { budgetId?: string };
type TxnForm = Partial<BudgetTransaction> & { transactionId?: string };

/**
 * A financial-year choice that survives leaving the screen. Plain useState
 * reset it to the current FY on every remount, so navigating away and back
 * read as the selection being cleared (CHM-125). Per-browser convenience
 * only — a blocked or cleared store just falls back to the default.
 */
const useStickyFy = (key: string, fallback: string): [string, (value: string) => void] => {
  const [value, setValue] = useState(() => {
    try { return localStorage.getItem(key) || fallback; } catch { return fallback; }
  });
  const set = useCallback((next: string) => {
    setValue(next);
    try { localStorage.setItem(key, next); } catch { /* storage unavailable */ }
  }, [key]);
  return [value, set];
};

const BudgetManagement = ({ isDarkMode }) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;

  // Write access comes from the same permission source the rest of the app
  // uses, rather than this module's own storage-key sniffing. It also honours a
  // per-module Read Only grant, so this module (and the budget-document editor
  // it gates with canEdit) goes view-only the same way the shared controls do.
  const moduleReadOnly = useReadOnly();
  const hasWriteAccess = !isGlobalReadOnly(getStoredUser()) && !moduleReadOnly;
  // Who may approve is decided by the server (Settings › Approvals: levels and
  // roles; by default one level, System Administrator), so the buttons show for
  // everyone here and a refusal explains itself. Proposer and approver must be
  // different people.
  const canApprove = true;
  const approverName = (getStoredUser()?.name || '').trim() || 'Administrator';

  const [activeTab, setActiveTab] = useState('dashboard');
  const [viewMode, setViewMode] = useState('history');
  const [bgtForm, setBgtForm] = useState<BudgetForm>(EMPTY_BUDGET);
  const [txnForm, setTxnForm] = useState<TxnForm>(EMPTY_TXN);
  const [editingId, setEditingId] = useState(null);
  /** The row being edited, so the update re-sends what the form does not show (creator, status...). */
  const [editingRow, setEditingRow] = useState<Budget | BudgetTransaction | null>(null);
  const [saving, setSaving] = useState(false);
  const [voiding, setVoiding] = useState(null);
  /** A deletion request an administrator is about to approve. */
  const [approvingDelete, setApprovingDelete] = useState<Budget | BudgetTransaction | null>(null);
  const [approving, setApproving] = useState<Approvable | null>(null);
  const [rejecting, setRejecting] = useState<Approvable | null>(null);
  /** Year filter for the Preparation and Adjustments lists; '' shows every year. */
  const [listFy, setListFy] = useStickyFy('church_budget_list_fy', currentFinancialYear());
  /** Budget vs Actual: one row per budget line, or spend by head per month. */
  const [dashboardView, setDashboardView] = useState<'lines' | 'months'>('lines');
  /** The Annual Budget Document has edits not yet saved; a tab switch must ask first. */
  const [docDirty, setDocDirty] = useState(false);
  const [pendingTab, setPendingTab] = useState<string | null>(null);
  const onDocDirty = useCallback((dirty: boolean) => setDocDirty(dirty), []);

  const isEditing = editingId !== null;
  const isDeletedView = viewMode === 'deleted';
  const isRequestedView = viewMode === 'requested';
  const { notification, notify, dismiss } = useNotification();

  // Years the parish listed under Settings › Dropdown Lists; heads and lines
  // come from the Annual Budget Document itself, not from a free list.
  const financialYears = useOptionList('church_bgt_fy');

  // Budget-vs-actual needs the two budget resources plus every finance record
  // booked elsewhere in the app.
  const { data: finance, loading, refetch } = useFetchList<FinanceRow>(async (p, o) => {
    const [bgt, txn, exp, don, col] = await Promise.all([
      api.get(budgetsApi.url(), o).catch(() => []),
      api.get(transactionsApi.url(), o).catch(() => []),
      api.get(expensesApi.url(), o).catch(() => []),
      api.get(donationsApi.url(), o).catch(() => []),
      api.get(collectionsApi.url(), o).catch(() => [])
    ]);
    return [
      ...getList<Budget>(bgt).map((x): FinanceRow => ({ ...x, __key: 'budgets' })),
      ...getList<BudgetTransaction>(txn).map((x): FinanceRow => ({ ...x, __key: 'transactions' })),
      ...getList<Expense>(exp).map((x): FinanceRow => ({ ...x, __key: 'expenses' })),
      ...getList<Donation>(don).map((x): FinanceRow => ({ ...x, __key: 'incomes' })),
      ...getList<Collection>(col).map((x): FinanceRow => ({ ...x, __key: 'incomes' }))
    ];
  });

  const groups = useMemo(() => {
    // Live is "not VOID", never "=== ACTIVE" — a row saved before the column
    // existed has no status and must still count towards actuals. Testing for
    // ACTIVE quietly dropped those from every variance figure on this screen.
    const ofKey = (key: FinanceRow['__key']) => finance.filter((x) => x.__key === key);
    return {
      budgets: liveByStatus(ofKey('budgets')).sort((a, b) => b.id - a.id),
      transactions: liveByStatus(ofKey('transactions')).sort((a, b) => b.id - a.id),
      expenses: liveByStatus(ofKey('expenses')),
      incomes: liveByStatus(ofKey('incomes')),
      voidedBudgets: voidedByStatus(ofKey('budgets')),
      voidedTransactions: voidedByStatus(ofKey('transactions')),
      requestedBudgets: deleteRequestedByStatus(ofKey('budgets')),
      requestedTransactions: deleteRequestedByStatus(ofKey('transactions'))
    };
  }, [finance]);


  // --- Budget vs Actual ----------------------------------------------------
  //
  // Built from the Annual Budget Document, not from the flat Budget Preparation
  // rows. Those never met the expenses: a budget was bucketed under its own
  // department while every expense was filed under "General Fund", so the two
  // could not land in the same bucket and Actual always came out zero.
  //
  // An expense now stores the head and item it was booked against, so a line and
  // its spending share a name by construction rather than by luck.
  const [budgetTree, setBudgetTree] = useState<BudgetTree | null>(null);
  const loadTree = useCallback(() => {
    budgetHeads.get()
      .then(setBudgetTree)
      .catch(() => setBudgetTree(null));
  }, []);
  useEffect(() => { loadTree(); }, [loadTree]);
  // The document tab saves its own copy; reload ours when leaving it so the
  // report and the expense-form pick lists see what was just saved.
  useEffect(() => { if (activeTab !== 'document') loadTree(); }, [activeTab, loadTree]);

  const documentYears = useMemo(() => Object.keys(budgetTree || {}).sort().reverse(), [budgetTree]);
  /** Years a proposal may be for: the document's, the next one, and any the parish listed. */
  const proposalYears = useMemo(() => {
    const latest = documentYears[0] || currentFinancialYear();
    return Array.from(new Set([...documentYears, nextFinancialYear(latest), ...financialYears.options].filter(Boolean)))
      .sort().reverse();
  }, [documentYears, financialYears.options]);
  const listYears = useMemo(() => {
    const years = new Set<string>(documentYears);
    groups.budgets.forEach((b) => years.add(String((b as Budget).fy || '').trim()));
    groups.transactions.forEach((x) => years.add(String((x as BudgetTransaction).fy || '').trim()));
    // Years the parish listed under Settings belong here too. Without them a
    // financial year that has no records yet could never be picked from this
    // filter, which read as the year missing from the dropdown (CHM-117).
    financialYears.options.forEach((y) => years.add(String(y || '').trim()));
    if (listFy) years.add(listFy);
    years.delete('');
    return Array.from(years).sort().reverse();
  }, [documentYears, groups.budgets, groups.transactions, listFy, financialYears.options]);
  const inListYear = useCallback(
    (r: unknown) => !listFy || String((r as { fy?: string }).fy || '').trim() === listFy,
    [listFy]
  );

  const [dashboardFy, setDashboardFy] = useStickyFy('church_budget_dashboard_fy', currentFinancialYear());
  // Budget Adjustments take part: an Income adjustment raises a head's
  // allotment, an Expense adjustment is spend against it.
  const varianceReport = useMemo(
    () => buildDocumentVariance(budgetTree, dashboardFy, groups.expenses, groups.transactions),
    [budgetTree, dashboardFy, groups.expenses, groups.transactions]
  );
  const burn = useMemo(
    () => monthlyBurn(budgetTree, dashboardFy, groups.expenses, groups.transactions),
    [budgetTree, dashboardFy, groups.expenses, groups.transactions]
  );
  /** Allotted vs spent per head, for the chart above the table. */
  const headTotals = useMemo(() => {
    const out = new Map<string, { head: string; allotted: number; spent: number }>();
    varianceReport.forEach((r) => {
      const entry = out.get(r.head) || { head: r.head, allotted: 0, spent: 0 };
      entry.allotted += r.budget;
      entry.spent += r.actual;
      out.set(r.head, entry);
    });
    return Array.from(out.values());
  }, [varianceReport]);
  /** Heads of the document for the chosen year — what an adjustment or expense should be filed under. */
  const documentHeadsFor = useCallback(
    (fy?: string) => headNamesIn(budgetTree, String(fy || '').trim()),
    [budgetTree]
  );
  const varianceSummary = useMemo(() => varianceTotals(varianceReport), [varianceReport]);

  /** Preparation totals for the chosen year, in annual terms. */
  const prepSummary = useMemo(() => {
    const rows = (groups.budgets as Budget[]).filter(inListYear);
    const total = (pick: (b: Budget) => boolean) => rows.filter(pick).reduce((sum, b) => sum + annualOfProposal(b), 0);
    return {
      approved: total((b) => b.workflowStatus === 'Approved'),
      pending: total((b) => b.workflowStatus === 'Pending'),
      pendingCount: rows.filter((b) => b.workflowStatus === 'Pending').length,
      rejected: total((b) => b.workflowStatus === 'Rejected')
    };
  }, [groups.budgets, inListYear]);

  /** Adjustment totals for the chosen year. */
  const adjSummary = useMemo(() => {
    const rows = (groups.transactions as BudgetTransaction[]).filter(inListYear);
    const total = (pick: (x: BudgetTransaction) => boolean) => rows.filter(pick).reduce((sum, x) => sum + (parseFloat(String(x.amount)) || 0), 0);
    return {
      supplementary: total((x) => approvalOf(x) === 'Approved' && x.type === 'Income'),
      charges: total((x) => approvalOf(x) === 'Approved' && x.type === 'Expense'),
      pending: total((x) => approvalOf(x) === 'Pending'),
      pendingCount: rows.filter((x) => approvalOf(x) === 'Pending').length
    };
  }, [groups.transactions, inListYear]);

  /** A head's budget for the year as it stands: the document lines plus approved supplementary adjustments. */
  const headBudgetNow = useCallback((fy?: string, head?: string): number | null => {
    const year = String(fy || '').trim();
    const name = String(head || '').trim().toLowerCase();
    const inDocument = headsIn(budgetTree, year).find((h) => String(h.headName || '').trim().toLowerCase() === name);
    if (!inDocument) return null;
    const base = (inDocument.items || []).reduce((sum, item) => sum + annualOf(item), 0);
    const adj = adjustmentsByHead(groups.transactions as BudgetTransaction[], year).get(name);
    return base + (adj?.budget || 0);
  }, [budgetTree, groups.transactions]);

  const dataset = useMemo<Array<VarianceRow | FinanceRow>>(() => {
    if (activeTab === 'dashboard') return varianceReport;
    // The budget document tab manages its own tree; it needs no shared dataset.
    if (activeTab === 'document') return [];
    if (activeTab === 'preparation') {
      return (isDeletedView ? groups.voidedBudgets : isRequestedView ? groups.requestedBudgets : groups.budgets).filter(inListYear);
    }
    return (isDeletedView ? groups.voidedTransactions : isRequestedView ? groups.requestedTransactions : groups.transactions).filter(inListYear);
  }, [activeTab, isDeletedView, isRequestedView, varianceReport, groups, inListYear]);

  const { searchTerm, setSearchTerm, filtered, clear } = useSearch<VarianceRow | FinanceRow>(
    dataset, ['head', 'item', 'department', 'dept', 'fy', 'voucherNo', 'budgetId', 'transactionId', 'notes', 'approvedBy']
  );
  const page = usePagination(filtered);

  // A printed report is the whole report: the screen shows one page of rows at
  // a time, the paper carries every row that the filters selected.
  const [printing, setPrinting] = useState(false);

  const resetForms = () => {
    const year = documentYears.includes(listFy) ? listFy : (documentYears[0] || currentFinancialYear());
    setBgtForm({ ...EMPTY_BUDGET, fy: year });
    setTxnForm({ ...EMPTY_TXN, fy: documentYears.includes(year) ? year : (documentYears[0] || '') });
    setEditingId(null);
    setEditingRow(null);
  };

  const goToTab = (tab) => {
    setActiveTab(tab);
    setViewMode('history');
    resetForms();
    page.reset();
    clear();
  };

  /** Leaving the document with unsaved edits would lose them, so ask first. */
  const switchTab = (tab) => {
    if (tab === activeTab) return;
    if (activeTab === 'document' && docDirty) { setPendingTab(tab); return; }
    goToTab(tab);
  };

  const switchView = (mode) => {
    if (mode !== 'add') resetForms();
    setViewMode(mode);
    page.reset();
  };

  // "Open the record" from the Approvals inbox: a proposal (Budget Preparation)
  // or an adjustment (Budget Adjustments). Neither has a view of its own — its
  // row, with its Approve / Reject, is the record — so the list is narrowed to
  // it: its year, its Active / Pending Deletion / Deleted list, its number.
  useOpenRecordFromLink(({ id, type, ref }) => {
    const keys = type === 'BUDGET' ? ['budgets'] : type === 'BUDGET_ADJUSTMENT' ? ['transactions'] : ['budgets', 'transactions'];
    const hits = finance.filter((r) => keys.includes(r.__key) && r.id === id);
    const row = (hits.length > 1 && ref ? hits.find((r) => Object.values(r).some((v) => v != null && String(v) === ref)) : null)
      || hits[0];
    if (!row) { notify(RECORD_GONE, 'error'); return; }
    const isBudget = row.__key === 'budgets';
    const number = isBudget ? (row as Budget).budgetId : (row as BudgetTransaction).transactionId;
    switchTab(isBudget ? 'preparation' : 'transactions');
    setViewMode(isVoidStatus(row) ? 'deleted' : isDeleteRequested(row) ? 'requested' : 'history');
    setListFy(String((row as { fy?: string }).fy || '').trim());
    setSearchTerm(String(number || ''));
    page.reset();
  }, !loading);

  const startEdit = (item) => {
    if (activeTab === 'preparation') {
      // An approved line already lives in the document; change it there.
      if (item.workflowStatus === 'Approved') { notify('This proposal is approved and in the Annual Budget Document — edit the document instead.', 'error'); return; }
      setBgtForm({
        fy: item.fy || '', department: item.department || '', head: item.head || '',
        amount: item.amount || '', isOneTime: item.isOneTime !== false,
        notes: item.notes || '', budgetId: item.budgetId
      });
    } else {
      if (approvalOf(item) === 'Approved') { notify('An approved adjustment cannot be edited. Delete it with a reason and record a new one.', 'error'); return; }
      setTxnForm({
        fy: item.fy || '', head: item.head || '',
        date: item.date || '', amount: item.amount || '', type: item.type || 'Income',
        voucherNo: item.voucherNo || '', notes: item.notes || '', transactionId: item.transactionId
      });
    }
    setEditingId(item.id);
    setEditingRow(item);
    setViewMode('add');
  };

  const guardWrite = () => {
    if (hasWriteAccess) return true;
    notify('Action Restricted: You only have view access for the Budget module.', 'error');
    return false;
  };

  const saveRecord = async (resource, payload, label) => {
    setSaving(true);
    try {
      if (isEditing) await resource.update(editingId, payload);
      else await resource.create(payload);
      await refetch();
      notify(isEditing ? `${label} updated successfully.` : `${label} saved successfully.`, 'success');
      resetForms();
      setViewMode('history');
    } catch (err) {
      notify(err.message || `Could not save the ${label.toLowerCase()}.`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleBudgetSubmit = async (e) => {
    e.preventDefault();
    if (!guardWrite()) return;

    const fy = String(bgtForm.fy || '').trim();
    const department = String(bgtForm.department || '').trim();
    const head = String(bgtForm.head || '').trim();
    const amount = parseFloat(String(bgtForm.amount)) || 0;
    if (!fy || !department || !head) { notify('Financial year, budget head and budget line are required.', 'error'); return; }
    if (amount <= 0) { notify('Enter the proposed amount.', 'error'); return; }

    financialYears.ensure(fy);

    // Sent back with the row it came from, so an edit keeps the creator and
    // status. A new proposal is made Pending by the server whatever is sent;
    // the blank id is filled from a sequence there too.
    await saveRecord(budgetsApi, {
      ...(editingRow || {}),
      ...bgtForm,
      fy, department, head, amount,
      isOneTime: Boolean(bgtForm.isOneTime),
      headType: 'Expense',
      budgetId: bgtForm.budgetId || undefined,
      status: 'ACTIVE' as const
    }, 'Proposal');
  };

  const handleTransactionSubmit = async (e) => {
    e.preventDefault();
    if (!guardWrite()) return;

    const fy = String(txnForm.fy || '').trim();
    const head = String(txnForm.head || '').trim();
    const amount = parseFloat(String(txnForm.amount)) || 0;
    if (!fy || !head) { notify('Choose the financial year and the budget head.', 'error'); return; }
    // Only a head of that year's document can be adjusted — anything else would
    // land under "Not in budget" on the report.
    if (!documentHeadsFor(fy).some((h) => h.trim().toLowerCase() === head.toLowerCase())) {
      notify(`"${head}" is not a head of the ${fy} Annual Budget Document.`, 'error'); return;
    }
    if (amount <= 0) { notify('Enter the amount.', 'error'); return; }
    if (!txnForm.date) { notify('Enter the date.', 'error'); return; }

    await saveRecord(transactionsApi, {
      ...(editingRow || {}),
      ...txnForm,
      fy, head, amount,
      // Kept equal to the head for anything still reading the old column.
      department: head,
      transactionId: txnForm.transactionId || undefined,
      status: 'ACTIVE' as const
    }, 'Adjustment');
  };

  /**
   * Approval is the server's act: it stamps who and when, and for a proposal
   * also writes the line into the Annual Budget Document in the same
   * transaction — so nothing approved can be left out of the report.
   */
  const confirmApprove = async () => {
    const target = approving;
    setApproving(null);
    if (!target) return;
    try {
      if (target.kind === 'budget') {
        const done = await budgetsApi.approve(target.row.id, approverName);
        loadTree();
        notify(done?.workflowStatus === 'Approved'
          ? `${target.row.budgetId} approved — the line is now in the ${target.row.fy} Annual Budget Document.`
          : `Your level is approved. ${target.row.budgetId} now waits for the next level (see Approvals).`, 'success');
      } else {
        const done = await transactionsApi.approve(target.row.id, approverName);
        notify(done?.approvalStatus === 'Approved'
          ? `${target.row.transactionId} approved — it now counts in Budget vs Actual.`
          : `Your level is approved. ${target.row.transactionId} now waits for the next level (see Approvals).`, 'success');
      }
      window.dispatchEvent(new Event('approvals-changed'));
      await refetch();
    } catch (err) {
      notify(err.message || 'Could not approve.', 'error');
    }
  };

  const confirmReject = async (reason) => {
    const target = rejecting;
    setRejecting(null);
    if (!target) return;
    try {
      if (target.kind === 'budget') await budgetsApi.reject(target.row.id, approverName, reason);
      else await transactionsApi.reject(target.row.id, approverName, reason);
      await refetch();
      notify('Rejected. The reason is kept on the record.', 'success');
    } catch (err) {
      notify(err.message || 'Could not reject.', 'error');
    }
  };

  const listResource = () => (activeTab === 'preparation' ? budgetsApi : transactionsApi);
  const labelOf = (item) => item?.budgetId || item?.transactionId || 'record';

  /** Step 1: file the request. The row stays live until an administrator decides. */
  const confirmVoid = async (reason) => {
    const item = voiding;
    setVoiding(null);
    // Guard: the dialog can only reach here with a row selected, but the
    // state is nulled before the await so TypeScript cannot prove it.
    if (!item) return;
    try {
      await listResource().update(item.id, { ...item, ...requestDeletion(reason, approverName) });
      await refetch();
      notify(`Deletion of ${labelOf(item)} requested. An administrator has to approve it before it moves to Deleted History.`, 'success');
    } catch (err) {
      notify(err.message || 'Could not request the deletion.', 'error');
    }
  };

  /** Step 2a: an administrator approves — only now is the row voided. */
  const confirmApproveDelete = async () => {
    const item = approvingDelete;
    setApprovingDelete(null);
    if (!item) return;
    try {
      await listResource().update(item.id, { ...item, ...approveDeletion(item, approverName) });
      await refetch();
      notify(`${labelOf(item)} deleted. Kept in Deleted History.`, 'success');
    } catch (err) {
      notify(err.message || 'Could not delete the record.', 'error');
    }
  };

  /** Step 2b: an administrator keeps it — the request is closed and the row is back to normal. */
  const keepRow = async (item) => {
    try {
      await listResource().update(item.id, { ...item, ...keepRecord() });
      await refetch();
      notify(`${labelOf(item)} kept; the deletion request is closed.`, 'success');
    } catch (err) {
      notify(err.message || 'Could not update the record.', 'error');
    }
  };

  const sub = { fontSize: '12px', color: t.textFaint };
  const statusColor = { good: t.income, warn: t.warning, bad: t.danger };

  /** How a row is labelled by what it gathers. */
  const kindNote: Record<VarianceRowKind, string | null> = {
    line: null,
    other: 'expenses under this head that match no budget line',
    adjustment: 'Income raises the allotment · Expense counts as spend',
    unbudgeted: 'category is not a head of this year\'s budget'
  };

  /** "▲ ahead of pace" / "▼ under pace" against an even spend over the year. */
  const paceCell = (r: BudgetLineVariance) => {
    if (r.expected === null) return <span style={{ color: t.textFaint }}>—</span>;
    const delta = r.actual - r.expected;
    const ahead = delta > 0;
    return (
      <span title={`Expected by today at an even pace: ${formatCurrencyPrecise(r.expected)}`}
        style={{ fontWeight: 'bold', color: ahead ? t.danger : t.income, whiteSpace: 'nowrap' }}>
        {ahead ? '▲ ahead' : '▼ under'} {formatCurrency(Math.abs(delta))}
      </span>
    );
  };

  const dashboardColumns: Array<Column<BudgetLineVariance>> = [
    {
      key: 'head',
      header: 'Budget Head',
      render: (r) => (
        <>
          <strong style={{ color: r.kind === 'unbudgeted' ? t.warning : undefined }}>{r.head}</strong><br />
          <span style={{ fontSize: '12px', color: t.textFaint }}>{r.fy}</span>
        </>
      )
    },
    {
      key: 'item',
      header: 'Budget Line',
      render: (r) => (
        <>
          <strong style={{ fontStyle: r.kind === 'line' ? 'normal' : 'italic' }}>{r.item}</strong>
          {kindNote[r.kind] && <div style={{ fontSize: '11px', color: t.textFaint }}>{kindNote[r.kind]}</div>}
        </>
      )
    },
    { key: 'budget', header: 'Annual Budget', align: 'right', nowrap: true, render: (r) => <strong>{formatCurrencyPrecise(r.budget)}</strong> },
    {
      key: 'actual',
      header: 'Actual Spent',
      align: 'right',
      nowrap: true,
      // Counted from the expenses booked against this line, never typed.
      render: (r) => <strong style={{ color: t.expense }}>{formatCurrencyPrecise(r.actual)}</strong>
    },
    {
      key: 'varianceAmt',
      header: 'Variance (Bal)',
      align: 'right',
      nowrap: true,
      render: (r) => (
        <span style={{ fontWeight: 'bold', color: r.varianceAmt < 0 ? t.danger : t.income }}>
          {formatCurrencyPrecise(r.varianceAmt)}
        </span>
      )
    },
    { key: 'variancePct', header: '% Utilized', align: 'center', render: (r) => (r.budget > 0 ? `${r.variancePct.toFixed(1)}%` : '—') },
    { key: 'expected', header: 'On pace?', align: 'right', nowrap: true, render: paceCell },
    {
      key: 'status',
      header: 'Status',
      align: 'center',
      render: (r) => (
        <div style={{
          width: '16px', height: '16px', borderRadius: '50%',
          backgroundColor: statusColor[r.status], margin: '0 auto'
        }} />
      )
    }
  ];

  /** Who approved or rejected, and when — or the reason, for a rejection. */
  const approvalNote = (status: string | undefined, by?: string, on?: string, reason?: string) => {
    if (status === 'Approved') return by ? <div style={sub}>by {by}{on ? ` · ${formatDate(on)}` : ''}</div> : null;
    if (status === 'Rejected') return <div style={{ ...sub, color: t.danger, fontStyle: 'italic' }}>{reason || 'No reason given'}{by ? ` — ${by}` : ''}</div>;
    return <div style={sub}>awaiting approval</div>;
  };

  /**
   * One round icon button per action, the name on hover — four labelled
   * buttons made every row tall and noisy. The glyphs come from the app's own
   * SVG set (the sidebar's), so they draw the same on every machine, unlike
   * emoji. Colour says what the action does: green approves, red rejects or
   * deletes, grey edits or keeps.
   */
  const iconButton = (icon: IconName, label: string, onClick: () => void, tone: string) => (
    <button
      type="button"
      key={label}
      title={label}
      aria-label={label}
      onClick={onClick}
      style={{
        width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: tone, backgroundColor: hexToRgba(tone, t.isDarkMode ? 0.22 : 0.12),
        border: `1px solid ${hexToRgba(tone, 0.45)}`
      }}
    >
      <Icon name={icon} size={16} strokeWidth={2.4} />
    </button>
  );

  /**
   * Approve/Reject for an administrator on a pending row; Edit and Request
   * delete until it is approved. A row someone asked to delete offers only the
   * administrator's decision.
   */
  const rowActions = (kind: Approvable['kind'], r, status: string) => {
    const target = { kind, row: r } as Approvable;
    const row = (buttons: React.ReactNode) => (
      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', alignItems: 'center' }}>{buttons}</div>
    );
    if (isDeleteRequested(r)) {
      return row(canApprove
        ? (
          <>
            {iconButton('trash', 'Approve delete', () => setApprovingDelete(r), t.danger)}
            {iconButton('reply', 'Keep record', () => keepRow(r), t.textMuted)}
          </>
        )
        : <span style={sub}>deletion requested</span>);
    }
    const pending = status === 'Pending';
    const editable = hasWriteAccess && status !== 'Approved';
    return row(
      <>
        {canApprove && pending && iconButton('check', 'Approve', () => setApproving(target), t.success)}
        {canApprove && pending && iconButton('x', 'Reject', () => setRejecting(target), t.danger)}
        {editable && iconButton('pencil', 'Edit', () => startEdit(r), t.textMuted)}
        {editable && iconButton('trash', 'Request delete', () => setVoiding(r), t.danger)}
        {!editable && hasWriteAccess && <span style={sub}>{kind === 'budget' ? 'in document' : 'locked'}</span>}
      </>
    );
  };

  /** The request trail: reason and who asked — shown on the pending and deleted lists. */
  const requestColumns = [
    { key: 'deleteReason', header: 'Reason', wrap: true, render: (r) => <span style={{ color: t.danger, fontStyle: 'italic' }}>{r.deleteReason || '-'}</span> },
    { key: 'deleteRequestedBy', header: 'Requested By', render: (r) => (<span style={sub}>{r.deleteRequestedBy || '-'}<br />{r.deleteRequestedAt || ''}</span>) }
  ];
  const voidColumns = [
    ...requestColumns,
    { key: 'deletedAt', header: 'Deleted On', render: (r) => (<span style={sub}>{r.deletedAt || '-'}{r.deleteApprovedBy && <><br />by {r.deleteApprovedBy}</>}</span>) }
  ];

  const preparationColumns = [
    { key: 'budgetId', header: 'Budget ID', nowrap: true, render: (r: Budget) => (<><strong>{r.budgetId}</strong><br /><span style={sub}>{r.fy}</span></>) },
    {
      key: 'department',
      header: 'Budget Head › Line',
      render: (r: Budget) => (<><strong>{r.department}</strong><br /><span style={{ fontSize: t.fontSize.md }}>{r.head}</span></>)
    },
    {
      key: 'amount',
      header: 'Proposed',
      align: 'right',
      nowrap: true,
      render: (r: Budget) => (
        <>
          <strong>{formatCurrencyPrecise(r.amount)}</strong>
          <span style={sub}> {r.isOneTime === false ? '/mo' : 'one-time'}</span>
          {r.isOneTime === false && <div style={sub}>{formatCurrencyPrecise(annualOfProposal(r))} / yr</div>}
        </>
      )
    },
    {
      key: 'workflowStatus',
      header: 'Approval',
      render: (r: Budget) => (
        <>
          <Badge tone={toneOf(r.workflowStatus)}>{statusLabel(r.workflowStatus)}</Badge>
          {approvalNote(r.workflowStatus, r.approvedBy, r.approvedOn, r.rejectReason)}
        </>
      )
    },
    { key: 'notes', header: 'Justification', wrap: true, render: (r: Budget) => r.notes || '-' },
    { key: 'createdBy', header: 'Proposed By', render: (r: Budget) => (
      <>{r.createdBy || '-'}{viewMode === 'history' && isDeleteRequested(r) && <div><Badge tone="warning">Deletion requested</Badge></div>}</>
    ) },
    ...(isDeletedView ? voidColumns : isRequestedView ? requestColumns : []),
    ...(hasWriteAccess && !isDeletedView
      ? [{ key: 'actions', header: 'Actions', align: 'right', render: (r: Budget) => rowActions('budget', r, r.workflowStatus || 'Pending') }]
      : [])
  ];

  const transactionColumns = [
    { key: 'transactionId', header: 'Txn ID', nowrap: true, render: (r: BudgetTransaction) => (<><strong>{r.transactionId}</strong><br /><span style={sub}>{formatDate(r.date)}</span></>) },
    {
      key: 'head',
      header: 'Budget Head',
      render: (r: BudgetTransaction) => {
        const now = headBudgetNow(r.fy, r.head);
        return (
          <>
            <strong>{r.head}</strong><br />
            <span style={sub}>{r.fy}{now === null ? ' · not in document' : ` · budget now ${formatCurrency(now)}`}</span>
          </>
        );
      }
    },
    {
      key: 'type',
      header: 'Type',
      render: (r: BudgetTransaction) => (
        <Badge tone={r.type === 'Income' ? 'accent' : 'neutral'}>{TXN_TYPE_LABEL[r.type as string] || r.type}</Badge>
      )
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      nowrap: true,
      render: (r: BudgetTransaction) => (
        <span style={{ fontWeight: 'bold', color: r.type === 'Income' ? t.income : t.expense }}>
          {r.type === 'Income' ? '+' : '−'} {formatCurrencyPrecise(r.amount)}
        </span>
      )
    },
    {
      key: 'approvalStatus',
      header: 'Approval',
      render: (r: BudgetTransaction) => (
        <>
          <Badge tone={toneOf(approvalOf(r))}>{statusLabel(approvalOf(r))}</Badge>
          {approvalNote(approvalOf(r), r.approvedBy, r.approvedOn, r.rejectReason)}
        </>
      )
    },
    { key: 'voucherNo', header: 'Voucher', render: (r: BudgetTransaction) => r.voucherNo || '-' },
    { key: 'notes', header: 'Narration', wrap: true, render: (r: BudgetTransaction) => r.notes || '-' },
    { key: 'createdBy', header: 'Entered By', render: (r: BudgetTransaction) => (
      <>{r.createdBy || '-'}{viewMode === 'history' && isDeleteRequested(r) && <div><Badge tone="warning">Deletion requested</Badge></div>}</>
    ) },
    ...(isDeletedView ? voidColumns : isRequestedView ? requestColumns : []),
    ...(hasWriteAccess && !isDeletedView
      ? [{ key: 'actions', header: 'Actions', align: 'right', render: (r: BudgetTransaction) => rowActions('txn', r, approvalOf(r)) }]
      : [])
  ];

  const columns: Array<Column<never>> = (activeTab === 'dashboard'
    ? dashboardColumns
    : activeTab === 'preparation' ? preparationColumns : transactionColumns) as Array<Column<never>>;

  /** The KPI card the dashboard uses, for the two list tabs. */
  const kpiCard = (label: string, value: string, accent: string, note?: string) => (
    <div key={label} style={{
      flex: '1 1 180px', backgroundColor: t.bgSubtle, padding: '12px 15px',
      borderRadius: t.radius.lg, borderLeft: `4px solid ${accent}`
    }}>
      <div style={{ fontSize: '12px', color: accent, fontWeight: 'bold', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: '22px', fontWeight: 900, color: t.textMain }}>{value}</div>
      {note && <div style={sub}>{note}</div>}
    </div>
  );

  return (
    <div style={{ ...styles.container, minHeight: '80vh' }}>
      <Notification notification={notification} onClose={dismiss} />

      <PageHeader
        icon="📈"
        title="Budget Management"
        activeTab={activeTab}
        onTabChange={switchTab}
        tabs={[
          { key: 'dashboard', label: '📊 Budget vs Actual' },
          { key: 'document', label: '📑 Annual Budget Document' },
          { key: 'preparation', label: '📝 Budget Preparation' },
          { key: 'transactions', label: '💳 Budget Adjustments' }
        ]}
        actions={!hasWriteAccess && (
          <span style={{ marginLeft: '12px' }}><Badge tone="warning">View only</Badge></span>
        )}
      />

      {activeTab !== 'dashboard' && activeTab !== 'document' && (
        <SubTabs
          value={viewMode}
          onChange={switchView}
          tabs={[
            { key: 'history', label: '📋 View Active Records' },
            {
              key: 'requested',
              label: `⏳ Pending Deletion (${(activeTab === 'preparation' ? groups.requestedBudgets : groups.requestedTransactions).length})`,
              variant: (activeTab === 'preparation' ? groups.requestedBudgets : groups.requestedTransactions).length ? 'danger' : undefined
            },
            { key: 'deleted', label: '🗑️ Deleted', variant: 'danger' },
            ...(hasWriteAccess
              ? [{ key: 'add', write: true, label: isEditing ? '✎ Editing Record...' : '+ Add New Entry' }]
              : [])
          ]}
        />
      )}

      {/* Budget vs Actual reads one financial year of the Annual Budget Document,
          so the year has to be chosen here rather than inferred. */}
      {activeTab === 'dashboard' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: t.spacing.sm, marginBottom: t.spacing.md, flexWrap: 'wrap' }}>
          <span style={{ fontSize: t.fontSize.base, color: t.textMuted, fontWeight: 'bold' }}>
            Financial Year:
          </span>
          <Select
            value={dashboardFy}
            onChange={(e) => setDashboardFy(e.target.value)}
            options={Object.keys(budgetTree || {}).sort().reverse()}
            placeholder={budgetTree ? '' : 'Loading...'}
            isDarkMode={isDarkMode}
            containerStyle={{ marginBottom: 0, minWidth: '180px' }}
          />
          {varianceReport.length === 0 && budgetTree && (
            <span style={{ color: t.textMuted, fontSize: t.fontSize.base }}>
              No budget lines for {dashboardFy}. Add them under Annual Budget Document.
            </span>
          )}
          <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto', flexWrap: 'wrap' }}>
            <Button variant="tab" size="sm" active={dashboardView === 'lines'} onClick={() => setDashboardView('lines')}>By line</Button>
            <Button variant="tab" size="sm" active={dashboardView === 'months'} onClick={() => setDashboardView('months')}>By month</Button>
            <PrintButton
              module="Budget Management"
              // The two views have different columns, so each is its own document.
              documentType={dashboardView === 'lines' ? 'BUDGET_REPORT' : 'BUDGET_MONTHLY_REPORT'}
              size="sm"
              disabled={!varianceReport.length}
              title={`Budget-${dashboardView === 'lines' ? 'vs-Actual' : 'by-Month'}-${dashboardFy}`}
              data={() => {
                const sum = (xs: number[]) => xs.reduce((a, b) => a + (Number(b) || 0), 0);
                if (dashboardView === 'lines') {
                  return {
                    title: 'Budget vs Actual',
                    reference: `FY ${dashboardFy}`,
                    rowCount: varianceReport.length,
                    totalBudget: formatCurrencyPrecise(sum(varianceReport.map((r) => r.budget))),
                    totalActual: formatCurrencyPrecise(sum(varianceReport.map((r) => r.actual))),
                    totalVariance: formatCurrencyPrecise(sum(varianceReport.map((r) => r.varianceAmt))),
                    rows: varianceReport.map((r) => {
                      const expected = (r as { expected?: number | null }).expected;
                      return {
                        particulars: `${r.head} — ${r.item}`,
                        budget: formatCurrencyPrecise(r.budget),
                        expected: expected === null || expected === undefined ? '—' : formatCurrencyPrecise(expected),
                        actual: formatCurrencyPrecise(r.actual),
                        variance: formatCurrencyPrecise(r.varianceAmt),
                        usedPct: r.budget > 0 ? `${Math.round(r.variancePct)}%` : '—',
                        amount: formatCurrencyPrecise(r.actual)
                      };
                    })
                  };
                }
                return {
                  title: 'Budget spend by month',
                  reference: `FY ${dashboardFy}`,
                  rowCount: burn.length,
                  totalSpent: formatCurrency(sum(burn.map((r) => r.total))),
                  totalAllotted: formatCurrency(sum(burn.map((r) => r.allotted))),
                  rows: burn.map((r) => ({
                    particulars: r.head,
                    ...Object.fromEntries(r.months.map((v, i) => [`m${i + 1}`, v ? formatCurrency(v) : '—'])),
                    allotted: r.allotted ? formatCurrency(r.allotted) : '—',
                    amount: formatCurrency(r.total),
                    total: formatCurrency(r.total)
                  }))
                };
              }}
              onNotify={notify}
              isDarkMode={isDarkMode}
            />
          </div>
        </div>
      )}

      {activeTab === 'dashboard' && varianceReport.length > 0 && (
        <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {[
            { label: 'Total Budget', value: varianceSummary.budget, accent: t.info },
            { label: 'Actual Spent', value: varianceSummary.actual, accent: t.expense },
            { label: 'Remaining', value: varianceSummary.remaining,
              accent: varianceSummary.remaining < 0 ? t.danger : t.income }
          ].map((kpi) => (
            <div key={kpi.label} style={{
              flex: '1 1 200px', backgroundColor: t.bgSubtle, padding: '15px',
              borderRadius: t.radius.lg, borderLeft: `4px solid ${kpi.accent}`
            }}>
              <div style={{ fontSize: '12px', color: kpi.accent, fontWeight: 'bold', textTransform: 'uppercase' }}>
                {kpi.label}
              </div>
              <div style={{ fontSize: '24px', fontWeight: 900, color: t.textMain }}>
                {formatCurrencyPrecise(kpi.value)}
              </div>
            </div>
          ))}
          {varianceSummary.expected > 0 && (
            <div style={{
              flex: '1 1 200px', backgroundColor: t.bgSubtle, padding: '15px',
              borderRadius: t.radius.lg, borderLeft: `4px solid ${varianceSummary.actual > varianceSummary.expected ? t.danger : t.income}`
            }}>
              <div style={{ fontSize: '12px', color: t.textMuted, fontWeight: 'bold', textTransform: 'uppercase' }}>
                Expected by today
              </div>
              <div style={{ fontSize: '24px', fontWeight: 900, color: t.textMain }}>
                {formatCurrencyPrecise(varianceSummary.expected)}
              </div>
              <div style={{ fontSize: '12px', color: varianceSummary.actual > varianceSummary.expected ? t.danger : t.income, fontWeight: 'bold' }}>
                {varianceSummary.actual > varianceSummary.expected ? '▲ spending ahead of pace' : '▼ spending under pace'}
              </div>
            </div>
          )}
          {varianceSummary.unbudgeted > 0 && (
            <div style={{
              flex: '1 1 200px', backgroundColor: t.bgSubtle, padding: '15px',
              borderRadius: t.radius.lg, borderLeft: `4px solid ${t.warning}`
            }}>
              <div style={{ fontSize: '12px', color: t.warning, fontWeight: 'bold', textTransform: 'uppercase' }}>
                Outside budget lines
              </div>
              <div style={{ fontSize: '24px', fontWeight: 900, color: t.textMain }}>
                {formatCurrencyPrecise(varianceSummary.unbudgeted)}
              </div>
              <div style={{ fontSize: '12px', color: t.textFaint }}>spent under no named line — see the italic rows</div>
            </div>
          )}
          {varianceSummary.overspent > 0 && (
            <div style={{
              flex: '1 1 200px', backgroundColor: t.bgSubtle, padding: '15px',
              borderRadius: t.radius.lg, borderLeft: `4px solid ${t.danger}`
            }}>
              <div style={{ fontSize: '12px', color: t.danger, fontWeight: 'bold', textTransform: 'uppercase' }}>
                Lines Over Budget
              </div>
              <div style={{ fontSize: '24px', fontWeight: 900, color: t.danger }}>
                {varianceSummary.overspent}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'document' && (
        <BudgetDocument isDarkMode={isDarkMode} canEdit={hasWriteAccess} onDirtyChange={onDocDirty} />
      )}

      {/* Allotted vs spent per head, before the line detail. */}
      {activeTab === 'dashboard' && dashboardView === 'lines' && headTotals.length > 0 && (
        <div style={{ height: 240, marginBottom: t.spacing.lg, backgroundColor: t.bgSubtle, borderRadius: t.radius.lg, padding: '10px 10px 0' }}>
          <ResponsiveContainer>
            <BarChart data={headTotals} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={t.border} vertical={false} />
              <XAxis dataKey="head" tick={{ fill: t.textMuted, fontSize: 11 }} interval={0} />
              <YAxis tick={{ fill: t.textMuted, fontSize: 11 }} tickFormatter={compactAxisTick} />
              <Tooltip contentStyle={{ backgroundColor: t.bgMain, borderColor: t.borderStrong, color: t.textMain, borderRadius: '8px' }}
                formatter={(v) => formatCurrency(Number(v))} />
              <Legend />
              <Bar dataKey="allotted" name="Allotted" fill={t.info} radius={[4, 4, 0, 0]} />
              <Bar dataKey="spent" name="Spent" fill={t.expense} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {activeTab === 'dashboard' && dashboardView === 'months' && (
        <div>
          <h3 style={{ color: t.textMain }}>Budget spend by month — FY {dashboardFy}</h3>
          <p style={{ color: t.textMuted, fontSize: t.fontSize.sm, margin: `0 0 ${t.spacing.md}` }}>
            What each head spent in each month. A cell is red when the month ran past
            the even monthly pace (allotment ÷ 12), so an overrun shows <em>when</em> it happened.
          </p>
          <div className="data-table-scroll" style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: t.fontSize.sm }}>
              <thead>
                <tr style={{ color: t.textMuted }}>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: `2px solid ${t.border}`, position: 'sticky', left: 0, backgroundColor: t.bgMain, color: t.textMuted }}>Head</th>
                  {FY_MONTHS.map((m) => <th key={m} style={{ textAlign: 'right', padding: '8px', borderBottom: `2px solid ${t.border}`, color: t.textMuted }}>{m}</th>)}
                  <th style={{ textAlign: 'right', padding: '8px', borderBottom: `2px solid ${t.border}`, color: t.textMuted }}>Total</th>
                  <th style={{ textAlign: 'right', padding: '8px', borderBottom: `2px solid ${t.border}`, color: t.textMuted }}>Allotted</th>
                </tr>
              </thead>
              <tbody>
                {burn.map((r) => (
                  <tr key={r.head}>
                    <td style={{ padding: '8px', borderBottom: `1px solid ${t.border}`, fontWeight: 'bold', whiteSpace: 'nowrap', position: 'sticky', left: 0, backgroundColor: t.bgMain, color: r.head === UNBUDGETED_HEAD ? t.warning : t.textMain }}>{r.head}</td>
                    {r.months.map((v, i) => (
                      <td key={i} className="tabular-nums" style={{
                        padding: '8px', textAlign: 'right', borderBottom: `1px solid ${t.border}`, whiteSpace: 'nowrap',
                        color: v === 0 ? t.textFaint : r.monthly > 0 && v > r.monthly ? t.danger : t.textMain,
                        fontWeight: r.monthly > 0 && v > r.monthly ? 'bold' : 'normal'
                      }}>
                        {v === 0 ? '·' : formatCurrency(v)}
                      </td>
                    ))}
                    <td className="tabular-nums" style={{ padding: '8px', textAlign: 'right', borderBottom: `1px solid ${t.border}`, fontWeight: 'bold', color: r.allotted > 0 && r.total > r.allotted ? t.danger : t.textMain, whiteSpace: 'nowrap' }}>{formatCurrency(r.total)}</td>
                    <td className="tabular-nums" style={{ padding: '8px', textAlign: 'right', borderBottom: `1px solid ${t.border}`, color: t.textMuted, whiteSpace: 'nowrap' }}>{r.allotted ? formatCurrency(r.allotted) : '—'}</td>
                  </tr>
                ))}
                {burn.length === 0 && (
                  <tr><td colSpan={15} style={{ padding: '30px', textAlign: 'center', color: t.textFaint }}>No budget heads for {dashboardFy}.</td></tr>
                )}
              </tbody>
              {burn.length > 0 && (
                <tfoot>
                  {/* Bold alone did not separate the total from the body rows
                      in dark mode. It now carries its own background, an
                      explicit colour and a rule above it (CHM-150). */}
                  <tr style={{ fontWeight: 'bold', backgroundColor: t.bgSubtle, color: t.textMain }}>
                    <td style={{
                      padding: '8px', position: 'sticky', left: 0,
                      backgroundColor: t.bgSubtle, borderTop: `2px solid ${t.borderStrong}`
                    }}>Total</td>
                    {FY_MONTHS.map((_, i) => (
                      <td key={i} className="tabular-nums" style={{ padding: '8px', textAlign: 'right', whiteSpace: 'nowrap', borderTop: `2px solid ${t.borderStrong}` }}>
                        {formatCurrency(burn.reduce((sum, r) => sum + r.months[i], 0))}
                      </td>
                    ))}
                    <td className="tabular-nums" style={{ padding: '8px', textAlign: 'right', color: t.expense, borderTop: `2px solid ${t.borderStrong}` }}>{formatCurrency(burn.reduce((sum, r) => sum + r.total, 0))}</td>
                    <td className="tabular-nums" style={{ padding: '8px', textAlign: 'right', borderTop: `2px solid ${t.borderStrong}` }}>{formatCurrency(burn.reduce((sum, r) => sum + r.allotted, 0))}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {(activeTab === 'preparation' || activeTab === 'transactions') && viewMode !== 'add' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: t.spacing.sm, marginBottom: t.spacing.md, flexWrap: 'wrap' }}>
            <span style={{ fontSize: t.fontSize.base, color: t.textMuted, fontWeight: 'bold' }}>Financial Year:</span>
            <Select
              value={listFy}
              onChange={(e) => { setListFy(e.target.value); page.reset(); }}
              options={listYears}
              placeholder="All years"
              isDarkMode={isDarkMode}
              containerStyle={{ marginBottom: 0, minWidth: '180px' }}
            />
            <span style={{ color: t.textMuted, fontSize: t.fontSize.sm, flex: '1 1 320px' }}>
              {activeTab === 'preparation'
                ? 'A proposal starts as Pending. An administrator approves or rejects it; an approved line goes into the Annual Budget Document by itself.'
                : 'An adjustment starts as Pending and counts in Budget vs Actual only after an administrator approves it.'}
            </span>
          </div>
          {!isDeletedView && (
            <div style={{ display: 'flex', gap: '15px', marginBottom: t.spacing.md, flexWrap: 'wrap' }}>
              {activeTab === 'preparation' ? [
                kpiCard('Approved', formatCurrencyPrecise(prepSummary.approved), t.income, 'in the budget document'),
                kpiCard('Awaiting approval', formatCurrencyPrecise(prepSummary.pending), t.warning, `${prepSummary.pendingCount} proposal${prepSummary.pendingCount === 1 ? '' : 's'}`),
                kpiCard('Rejected', formatCurrencyPrecise(prepSummary.rejected), t.danger)
              ] : [
                kpiCard('Supplementary approved', formatCurrencyPrecise(adjSummary.supplementary), t.income, 'added to heads\' budgets'),
                kpiCard('Charges approved', formatCurrencyPrecise(adjSummary.charges), t.expense, 'counted as spent'),
                kpiCard('Awaiting approval', formatCurrencyPrecise(adjSummary.pending), t.warning, `${adjSummary.pendingCount} adjustment${adjSummary.pendingCount === 1 ? '' : 's'}`)
              ]}
            </div>
          )}
        </>
      )}

      {activeTab !== 'document' && (activeTab === 'dashboard' ? dashboardView === 'lines' : viewMode !== 'add') && (
        <div>
          {activeTab === 'dashboard' && (
            <h3 style={{ color: t.textMain }}>Budget vs Actual — FY {dashboardFy}</h3>
          )}
          <div>
            <SearchBar
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); page.reset(); }}
              onClear={() => { clear(); page.reset(); }}
              placeholder={activeTab === 'dashboard'
                ? 'Search by Head or Line...'
                : 'Search by ID, Head, Line, Voucher, Notes or Approver...'}
            />
          </div>

          <DataTable
            columns={columns}
            rows={printing ? filtered : page.currentItems}
            rowKey={(row, idx) =>
              (row as FinanceRow).id ?? `${(row as VarianceRow).head}-${(row as BudgetLineVariance).item}-${(row as VarianceRow).fy}-${idx}`}
            loading={loading}
            emptyMessage="No financial data found."
          />

          <div>
            <Pagination {...page} itemsOnPage={page.currentItems.length} label="records" />
          </div>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(pendingTab)}
        title="Unsaved budget changes"
        message="The Annual Budget Document has edits that are not saved. Leave this tab and lose them?"
        confirmLabel="Leave without saving"
        cancelLabel="Stay"
        onConfirm={() => { const tab = pendingTab; setPendingTab(null); setDocDirty(false); if (tab) goToTab(tab); }}
        onCancel={() => setPendingTab(null)}
      />

      {viewMode === 'add' && activeTab === 'preparation' && hasWriteAccess && (
        <FormLayout
          title={isEditing ? `Edit Proposal ${bgtForm.budgetId || ''}` : 'Propose a Budget Line'}
          submitLabel={isEditing ? 'Update Proposal' : 'Submit for Approval'}
          submitting={saving}
          onSubmit={handleBudgetSubmit}
          onCancel={isEditing ? () => switchView('history') : undefined}
          cancelLabel="Cancel Edit"
        >
          <p style={{ color: t.textMuted, fontSize: t.fontSize.md, margin: '0 0 12px' }}>
            Pick the head and the line this money is for. An existing line will have its amount replaced when
            approved; a new name creates the head or line in the Annual Budget Document.
          </p>
          <FormLayout.Grid>
            <SearchableSelect
              label="Financial Year" required placeholder="e.g. 2027-2028"
              value={bgtForm.fy}
              onChange={(v) => setBgtForm((prev) => ({ ...prev, fy: v, department: '', head: '' }))}
              options={proposalYears} allowManage={false}
            />
            <SearchableSelect
              label="Budget Head" required placeholder="Choose a head, or type a new one..."
              value={bgtForm.department}
              onChange={(v) => setBgtForm((prev) => ({ ...prev, department: v, head: '' }))}
              options={documentHeadsFor(bgtForm.fy)} allowManage={false}
            />
            <SearchableSelect
              label="Budget Line" required fullWidth
              placeholder={bgtForm.department ? 'Choose a line under this head, or type a new one...' : 'Choose a head first'}
              disabled={!bgtForm.department}
              value={bgtForm.head}
              onChange={(v) => setBgtForm((prev) => ({ ...prev, head: v }))}
              options={itemNamesUnder(budgetTree, String(bgtForm.fy || '').trim(), bgtForm.department)} allowManage={false}
            />
            <Input
              label={`Proposed Amount (${currencySymbol()})`} type="number" step="0.01" required placeholder="0.00"
              value={bgtForm.amount}
              onChange={(e) => setBgtForm({ ...bgtForm, amount: e.target.value })}
              style={{ fontWeight: 'bold' }}
              hint={bgtForm.isOneTime
                ? 'The whole year\'s figure.'
                : `Per month — ${formatCurrencyPrecise(annualOfProposal({ amount: bgtForm.amount, isOneTime: false }))} for the year.`}
            />
            <Select
              label="Amount is" placeholder=""
              options={AMOUNT_BASIS}
              value={bgtForm.isOneTime ? 'onetime' : 'monthly'}
              onChange={(e) => setBgtForm({ ...bgtForm, isOneTime: e.target.value === 'onetime' })}
            />
            <Input
              label="Justification" type="textarea" rows={2} fullWidth
              placeholder="Why this allocation is needed..."
              value={bgtForm.notes}
              onChange={(e) => setBgtForm({ ...bgtForm, notes: e.target.value })}
            />
          </FormLayout.Grid>
        </FormLayout>
      )}

      {viewMode === 'add' && activeTab === 'transactions' && hasWriteAccess && (
        <FormLayout
          title={isEditing ? `Edit Adjustment ${txnForm.transactionId || ''}` : 'Record a Budget Adjustment'}
          submitLabel={isEditing ? 'Update Adjustment' : 'Submit for Approval'}
          submitting={saving}
          onSubmit={handleTransactionSubmit}
          onCancel={isEditing ? () => switchView('history') : undefined}
          cancelLabel="Cancel Edit"
        >
          {documentYears.length === 0 && (
            <p style={{ color: t.warning, fontSize: t.fontSize.md, margin: '0 0 12px' }}>
              There is no Annual Budget Document yet, so there is no head to adjust. Create the document first.
            </p>
          )}
          <FormLayout.Grid>
            <Select
              label="Financial Year" required placeholder="Choose the year..."
              options={documentYears}
              value={txnForm.fy as string}
              onChange={(e) => setTxnForm({ ...txnForm, fy: e.target.value, head: '' })}
            />
            <SearchableSelect
              label="Budget Head" required
              placeholder={documentHeadsFor(txnForm.fy).length ? 'Choose a head of the document...' : 'No heads in this year\'s document'}
              disabled={!documentHeadsFor(txnForm.fy).length}
              value={txnForm.head}
              onChange={(v) => setTxnForm((prev) => ({ ...prev, head: v }))}
              options={documentHeadsFor(txnForm.fy)} allowAdd={false} allowManage={false}
            />
            <Select
              label="Type" placeholder="" fullWidth
              options={TXN_TYPES}
              value={txnForm.type as string}
              onChange={(e) => setTxnForm({ ...txnForm, type: e.target.value as HeadType })}
              hint={txnForm.head && headBudgetNow(txnForm.fy, txnForm.head) !== null
                ? `${txnForm.head} budget now: ${formatCurrencyPrecise(headBudgetNow(txnForm.fy, txnForm.head) || 0)}${txnForm.type === 'Income' && parseFloat(String(txnForm.amount)) > 0 ? ` → ${formatCurrencyPrecise((headBudgetNow(txnForm.fy, txnForm.head) || 0) + (parseFloat(String(txnForm.amount)) || 0))} once approved` : ''}`
                : undefined}
            />
            <Input
              label={`Amount (${currencySymbol()})`} type="number" step="0.01" required placeholder="0.00"
              value={txnForm.amount}
              onChange={(e) => setTxnForm({ ...txnForm, amount: e.target.value })}
              style={{ fontWeight: 'bold', color: txnForm.type === 'Income' ? t.income : t.expense }}
            />
            <Input
              label="Date" type="date" required
              value={txnForm.date}
              onChange={(e) => setTxnForm({ ...txnForm, date: e.target.value })}
            />
            <Input
              label="Voucher / Resolution No." placeholder="e.g. PC/2026/14"
              value={txnForm.voucherNo}
              onChange={(e) => setTxnForm({ ...txnForm, voucherNo: e.target.value })}
            />
            <Input
              label="Narration" type="textarea" rows={2} fullWidth
              placeholder="What this adjustment covers, and who resolved it..."
              value={txnForm.notes}
              onChange={(e) => setTxnForm({ ...txnForm, notes: e.target.value })}
            />
          </FormLayout.Grid>
        </FormLayout>
      )}

      <ConfirmDialog
        open={Boolean(approving)}
        title={approving?.kind === 'budget' ? `Approve ${approving.row.budgetId}` : `Approve ${(approving?.row as BudgetTransaction | undefined)?.transactionId || ''}`}
        message={approving?.kind === 'budget'
          ? `${approving.row.department} › ${approving.row.head}: ${formatCurrencyPrecise(annualOfProposal(approving.row))} for ${approving.row.fy}. The line is written into the Annual Budget Document straight away.`
          : approving
            ? `${TXN_TYPE_LABEL[String(approving.row.type)] || approving.row.type} of ${formatCurrencyPrecise(approving.row.amount)} on ${approving.row.head} (${approving.row.fy}). It will count in Budget vs Actual from now on.`
            : ''}
        confirmLabel="Approve"
        cancelLabel="Not now"
        destructive={false}
        onConfirm={confirmApprove}
        onCancel={() => setApproving(null)}
      />

      <PromptDialog
        open={Boolean(rejecting)}
        title={rejecting?.kind === 'budget' ? `Reject ${rejecting.row.budgetId}` : `Reject ${(rejecting?.row as BudgetTransaction | undefined)?.transactionId || ''}`}
        message="Give the reason. It stays on the record for the person who proposed it."
        placeholder="Reason for rejecting"
        confirmLabel="Reject"
        requiredError="A reason is required to reject."
        onSubmit={confirmReject}
        onCancel={() => setRejecting(null)}
      />

      <PromptDialog
        open={Boolean(voiding)}
        title={`Request deletion of ${labelOf(voiding)}`}
        message="Give the reason. An administrator will approve or refuse the request; until then the record stays active."
        placeholder="Reason for deletion"
        confirmLabel="Send request"
        requiredError="A reason is required to delete a financial record."
        onSubmit={confirmVoid}
        onCancel={() => setVoiding(null)}
      />

      <ConfirmDialog
        open={Boolean(approvingDelete)}
        title={`Delete ${labelOf(approvingDelete)}?`}
        message={approvingDelete
          ? `Requested by ${approvingDelete.deleteRequestedBy || 'unknown'}: "${approvingDelete.deleteReason || ''}". It moves to Deleted History and stops counting anywhere.`
          : ''}
        confirmLabel="Approve delete"
        cancelLabel="Not now"
        onConfirm={confirmApproveDelete}
        onCancel={() => setApprovingDelete(null)}
      />
    </div>
  );
};

export default BudgetManagement;
